import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { POSITIONS, BENEFITS, IMAGES } from "../lib/content";
import { Seo } from "../components/Seo";

const field =
  "w-full bg-transparent border-b border-gray-300 py-4 text-darkgray placeholder:text-darkgray/40 focus:border-gold focus:outline-none transition-colors duration-300 font-light";
const label = "block text-[0.68rem] uppercase tracking-[0.2em] text-darkgray/50 font-semibold mb-2";

const EMPTY = { name: "", email: "", phone: "", position: "", experience: "", linkedin: "", message: "" };

export default function Careers() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/careers", form);
      toast.success("Application received — thank you for your interest in Next Horizon Builders.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Careers | Next Horizon Builders"
        description="Explore career opportunities with Next Horizon Builders and join a professional construction team building multifamily communities across Southern California."
        path="/careers"
        image="/media/team/team1.jpg"
      />
      <PageHero
        eyebrow="Careers"
        titleLines={["Build your", "career here."]}
        subtitle="Join a team that treats construction as a craft. We're always looking for exceptional builders to grow with us across Southern California."
        image={IMAGES.team[1]}
        imageAlt="Next Horizon Builders construction crew on a Southern California multifamily jobsite"
      />

      {/* Culture */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <div className="relative overflow-hidden aspect-[4/3]">
            <img src={IMAGES.team[0]} alt="Our team" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div>
            <SectionHeading
              eyebrow="Life at Next Horizon"
              title={<>Where builders<br />become leaders.</>}
              subtitle="We invest in our people the way we invest in our projects — with intention, discipline, and a long-term view. From the field to the boardroom, careers here are built to last."
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-lightgray py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Why Join Us" title="Benefits that reflect your value." />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div className="bg-lightgray hover:bg-white transition-colors duration-300 p-9 md:p-11 h-full">
                  <h3 className="font-serif font-light text-2xl md:text-3xl text-navy tracking-tight">{b.title}</h3>
                  <p className="mt-4 text-darkgray/70 leading-relaxed font-light">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="bg-white py-24 md:py-36" id="apply">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionHeading eyebrow="Apply Now" title={<>Join our<br />team.</>} subtitle="Submit your details and our talent team will reach out about current and upcoming openings." />
            </div>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={submit} data-testid="careers-form" className="bg-lightgray p-8 md:p-14">
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <div>
                    <label className={label} htmlFor="k-name">Full Name *</label>
                    <input id="k-name" data-testid="careers-name" required value={form.name} onChange={set("name")} className={field} placeholder="Alex Rivera" />
                  </div>
                  <div>
                    <label className={label} htmlFor="k-email">Email *</label>
                    <input id="k-email" data-testid="careers-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="alex@email.com" />
                  </div>
                  <div>
                    <label className={label} htmlFor="k-phone">Phone</label>
                    <input id="k-phone" data-testid="careers-phone" value={form.phone} onChange={set("phone")} className={field} placeholder="(000) 000-0000" />
                  </div>
                  <div>
                    <label className={label} htmlFor="k-position">Position *</label>
                    <select id="k-position" data-testid="careers-position" required value={form.position} onChange={set("position")} className={`${field} cursor-pointer`}>
                      <option value="">Select a position</option>
                      {POSITIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={label} htmlFor="k-exp">Years of Experience</label>
                    <input id="k-exp" data-testid="careers-experience" value={form.experience} onChange={set("experience")} className={field} placeholder="e.g. 8 years" />
                  </div>
                  <div>
                    <label className={label} htmlFor="k-linkedin">LinkedIn / Portfolio</label>
                    <input id="k-linkedin" data-testid="careers-linkedin" value={form.linkedin} onChange={set("linkedin")} className={field} placeholder="linkedin.com/in/..." />
                  </div>
                  <div className="md:col-span-2">
                    <label className={label} htmlFor="k-message">Tell us about yourself</label>
                    <textarea id="k-message" data-testid="careers-message" rows={4} value={form.message} onChange={set("message")} className={`${field} resize-none`} placeholder="Share your background and what you're looking for." />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="careers-submit"
                  className="group mt-10 inline-flex items-center gap-3 bg-gold text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
